import { logger } from '@core/utils/logger';
export interface JwtPayload {
  exp?: number;
  iat?: number;
  sub?: string;
  role?: string;
  roles?: string[];
  name?: string;
  participantId?: string;
  tableSessionId?: string;
  foodVenueId?: string;
  [claim: string]: unknown;
}

export class JwtUtils {
  /**
   * Decodifica un JWT y retorna su payload
   */
  static decodeJWT(token: string): JwtPayload | null {
    if (!token) {
      logger.warn('Sesión', 'Token vacío');
      return null;
    }

    try {
      const parts = token.split('.');

      if (parts.length !== 3) {
        logger.error('Sesión', 'Token inválido: debe tener 3 partes');
        return null;
      }

      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

      const jsonPayload = decodeURIComponent(
        window
          .atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      );

      return JSON.parse(jsonPayload);
    } catch (error) {
      logger.error('Sesión', 'No se pudo decodificar el token', error);
      return null;
    }
  }

  /**
   * Verifica si un token decodificado está expirado
   */
  static isTokenExpired(decodedToken: JwtPayload | null): boolean {
    // Si no hay token o no tiene exp, considerarlo expirado
    if (!decodedToken || !decodedToken.exp) {
      return true; // ← FIX: Cambiar false por true
    }

    try {
      const expirationDate = new Date(decodedToken.exp * 1000);
      const now = new Date();
      return expirationDate <= now;
    } catch (error) {
      logger.error('Sesión', 'No se pudo verificar el vencimiento del token', error);
      return true;
    }
  }

  /**
   * Obtiene un claim específico del token
   */
  static getClaimValue<T = string>(token: string, claimName: string): T | null {
    const decoded = this.decodeJWT(token);
    return (decoded?.[claimName] ?? null) as T | null;
  }

  /**
   * Valida si un token es válido (bien formado y no expirado)
   */
  static isValidToken(token: string): boolean {
    if (!token || typeof token !== 'string') {
      return false;
    }

    const decoded = this.decodeJWT(token);
    if (!decoded) {
      return false;
    }

    return !this.isTokenExpired(decoded); // Más legible con negación
  }

  /**
   * Extrae múltiples claims de un token
   */
  static extractClaims(token: string, claimNames: string[]): Record<string, unknown> {
    const decoded = this.decodeJWT(token);

    if (!decoded) {
      return {};
    }

    // Usar reduce para ser más funcional
    return claimNames.reduce(
      (acc, claim) => {
        acc[claim] = decoded[claim] ?? null;
        return acc;
      },
      {} as Record<string, unknown>,
    );
  }
}
